import {ChangeEvent, ReactNode, useMemo, useState} from 'react'
import {StyledInputText, StyledInputTextGroup, StyledInputWrapper} from '../input-text/style'
import {getCountries, getCountryCallingCode, Value} from 'react-phone-number-input'
import {Label} from '@/components/ui/label/Label'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {CSSProperties} from 'styled-components'
import {StyledCallingCodeSelectTrigger, StyledPhoneInput} from './style'
import {SelectProvider} from '@ariakit/react'
import {CountryCode} from 'libphonenumber-js'
import {SelectItem, SelectPopover, SelectVirtualList} from '@/components/ui/select-atoms/SelectAtoms'
import {COUNTRY_LABELS} from '@/translations/i18n'
import i18n from 'i18next'

type InputPhoneProps = {
    className?: string
    label?: ReactNode
    name?: string
    typeIcon?: ReactNode
    touched?: boolean
    errorMessage?: string
    defaultCallingCountry?: CountryCode
    fullWidth?: boolean
    onChange: (value: {target: {value?: Value}}) => void
    onBlur?: (value: ChangeEvent<HTMLInputElement>) => void
    onCallingCountryChangeCb?: () => void
    successMessage?: string
    disabled?: boolean
    helpText?: string
    placeholder?: string
    inputSize?: 'sm' | 'md' | 'lg'
    width?: CSSProperties['width']
}

export const InputPhone = ({
    label,
    name,
    typeIcon,
    touched = false,
    errorMessage,
    fullWidth = false,
    successMessage,
    helpText,
    className,
    disabled,
    placeholder,
    onChange,
    onBlur,
    onCallingCountryChangeCb,
    defaultCallingCountry = 'IT',
    inputSize = 'md',
    width = '100%',
    ...rest
}: InputPhoneProps) => {
    const [callingCountry, setCallingCountry] = useState(defaultCallingCountry)
    const labels = useMemo(() => COUNTRY_LABELS[i18n.language as keyof typeof COUNTRY_LABELS], [i18n.language])

    const countries = getCountries().sort((a, b) => labels[a].localeCompare(labels[b]))

    return (
        <Flexbox direction="column" gap={1.5} fullWidth={fullWidth}>
            {label && <Label htmlFor={name}>{label}</Label>}
            <Flexbox gap={1.5} fullWidth={fullWidth}>
                <SelectProvider
                    setValue={(value: CountryCode) => {
                        onCallingCountryChangeCb?.()
                        setCallingCountry(value)
                    }}
                    value={callingCountry}
                >
                    <StyledCallingCodeSelectTrigger
                        placeholder="+39"
                        fullWidth={fullWidth}
                        value={callingCountry}
                        hasError={!!errorMessage}
                        displayValue={() => `+${getCountryCallingCode(callingCountry)}`}
                    />
                    <SelectPopover sameWidth={false}>
                        <SelectVirtualList
                            data={countries}
                            overscan={100}
                            style={{width: 300, height: 200}}
                            itemContent={(itemIndex, item) => (
                                <SelectItem hasItemCheckbox key={itemIndex} value={item}>
                                    {labels[item]} +{getCountryCallingCode(item)}
                                </SelectItem>
                            )}
                        />
                    </SelectPopover>
                </SelectProvider>

                <StyledInputText className={className} $width={width} direction="column" gap={1.5}>
                    <StyledInputTextGroup align="center" fullWidth $hasError={!!errorMessage}>
                        <StyledInputWrapper hasError={!!errorMessage} $size={inputSize}>
                            {typeIcon}
                            <StyledPhoneInput
                                name={name}
                                country={callingCountry}
                                onChange={value => onChange({target: {value}})}
                                onBlur={onBlur}
                                placeholder={placeholder}
                                disabled={disabled}
                                {...rest}
                            />
                        </StyledInputWrapper>
                    </StyledInputTextGroup>
                </StyledInputText>
            </Flexbox>
            {(helpText || errorMessage || successMessage) && (
                <Flexbox align="center" gap={errorMessage || successMessage ? 1.5 : 0}>
                    {(successMessage || errorMessage) && (
                        <InputStatusIcon touched={touched} hasError={!!errorMessage} />
                    )}
                    <InputHelpText error={errorMessage} success={successMessage} helpText={helpText} />
                </Flexbox>
            )}
        </Flexbox>
    )
}
